<template>
	<core-modal
		name="lead-export-modal"
		class="omapi-leadexport__wrapper"
		:headerText="modalTitle"
		:width="680"
		@closed="reset"
	>
		<modals-content class="omapi-multi-column flex omapi-leadmodal__content">
			<!-- Export Options -->
			<div id="export-options" class="export-options-slide" :class="{ active: currentSlide === 'options' }">
				<!-- Export All -->
				<div class="export-option">
					<div class="export-option__details">
						<h3 class="export-option__details-title omapi-document">Export All</h3>
						<p class="export-option__details-description">
							Export all contacts from all of your campaigns.
						</p>
					</div>
					<div class="export-option__check">
						<button class="export-button" :class="{ selected: selectedOption === 'all' }" @click="selectOption('all')"><span>Export All</span></button>
					</div>
				</div>
				<!-- Export Current View -->
				<div class="export-option">
					<div class="export-option__details">
						<h3 class="export-option__details-title omapi-document-alt">Export Current View</h3>
						<p class="export-option__details-description">
							Export only the contacts you are currently viewing.
						</p>
					</div>
					<div class="export-option__check">
						<button class="export-button" :class="{ selected: selectedOption === 'view' }" @click="selectOption('view')"><span>Export View</span></button>
					</div>
				</div>
				<div class="export-options-slide__action">
					<core-button color="green" @click="goTo('dataOptions')">
						<span class="omapi-link-arrow-after">Next</span>
					</core-button>
				</div>
			</div>
			<!-- Export Data Options -->
			<div id="export-options" class="export-options-slide" :class="{ active: currentSlide === 'dataOptions' }">
				<div class="export-option__details">
					<h3 class="export-option__details-title">Select Your Data Options</h3>
					<p class="export-option__details-description">
						Select from the options below that would like included in your export.
					</p>
					<div class="data-options-list">
						<common-selectable-input
							type="checkbox"
							v-for="(name, key) in dataOptions"
							:key="key"
							:value="key"
							:checked="isChecked(key)"
							@input="handleCheck(key)"
						>
							{{ name }}
						</common-selectable-input>
					</div>
				</div>
				<div class="export-options-slide__action">
					<core-button color="green" @click="runExport" :disabled="fetchingCount">
						<span class="omapi-link-arrow-after">Export</span>
					</core-button>
				</div>
			</div>
			<!-- Confirmation -->
			<div id="export-options" class="export-options-slide" :class="{ active: currentSlide === 'confirmation' }">
				<div class="export-option__details">
					<p class="export-option__details-warning">
						You are trying to export a large number of leads ({{ totalLeads }}). Please confirm this export or use the filters to narrow the results. We will send you an email with the link to download the file when it's ready.
					</p>
				</div>
				<div class="export-options-slide__action">
					<core-button color="green" @click="sendExportRequest">
						Yes, send me an email
					</core-button>
					<core-button color="red" @click="narrowResults">
						No, I want to narrow the results first
					</core-button>
				</div>
			</div>
		</modals-content>
	</core-modal>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import store from '@/store'

const defaultState = () => ({
	// Limit defined in the app:
	// @see https://github.com/awesomemotive/optin-monster-app/blob/fa3656bfd42b83055065dfcaec8eea729ef8ab47/themes/omappv4/src/Controller/MonsterLeadsController.php#L103-L105
	exportLimit    : 25000,
	selectedOption : 'all',
	currentSlide   : 'options',
	dataOptions    : {
		first_name  : 'First Name',
		last_name   : 'Last Name',
		email       : 'Email Address',
		phone       : 'Phone',
		campaign    : 'Campaign Title',
		site        : 'Site Name',
		site_domain : 'Site Domain',
		page_title  : 'Referring Page Title',
		page_url    : 'Referring Page URL',
		ip          : 'IP Address',
		date        : 'Date',
		time        : 'Time',
	},
	selectedDataOptions : [
		'first_name',
		'last_name',
		'email',
		'phone',
		'campaign',
		'site',
		'site_domain',
		'page_title',
		'page_url',
		'ip',
		'date',
		'time',
	],
	exportFilters : {
		dateStart : null,
		dateEnd   : null,
		siteIds   : store.getters['monsterleads/leadsQueryArgs'].siteIds,
		campaigns : [],
		tags      : [],
	},
	params        : {},
	format        : 'csv',
	exportArgs    : {},
	totalLeads    : 1,
	fetchingCount : false,
})

export default {
	data () {
		return defaultState()
	},
	watch : {
		dateRange () {
			this.selectOption('view')
		},
		filterOptions () {
			this.selectOption('view')
		},
		currentSlide (slide) {
			if ('dataOptions' === slide) {
				if ('view' === this.selectedOption) {
					this.setupCurrentViewParams()
				} else {
					this.setCount()
				}
			}
		},
	},
	computed : {
		...mapState([ 'user' ]),
		...mapState('monsterleads', [ 'filterOptions', 'export', 'count', 'dateRange', 'exportCount' ]),
		...mapGetters('monsterleads', [ 'leadsQueryArgs' ]),
		modalTitle () {
			return 'confirmation' === this.currentSlide ? 'Export Confirmation' : 'Export Options'
		},
	},
	methods : {
		...mapActions('monsterleads', {
			fetchTotalCount : 'fetchTotalCount',
			exportLeads     : 'export',
		}),
		/**
		 * Reset modal state.
		 * @returns {void}
		 */
		reset () {
			const state = defaultState()
			Object.keys(state).map(key => {
				this[ key ] = state[ key ]
			})
		},
		/**
		 * Set the selected option
		 * @param {string} option The selected option
		 * @returns {void}
		 */
		selectOption (option) {
			this.selectedOption = option
		},
		/**
		 * Go to slide
		 * @param {string} slide The slide
		 * @returns {void}
		 */
		goTo (slide) {
			this.currentSlide = slide
		},
		/**
		 * Is the data option selected?
		 * @param {string} key The data option
		 * @returns {boolean} Is the passed option selected
		 */
		isChecked (key) {
			return -1 !== this.selectedDataOptions.indexOf(key)
		},
		/**
		 * Handle Check, add or remove from selected options.
		 * @param {string} key The option
		 * @returns {void}
		 */
		handleCheck (key) {
			if (this.isChecked(key)) {
				this.selectedDataOptions.splice(this.selectedDataOptions.indexOf(key), 1)
			} else {
				this.selectedDataOptions.push(key)
			}
		},
		/**
		 * Setup Current View Parameters
		 * @returns {void}
		 */
		setupCurrentViewParams () {
			this.exportFilters = { ...this.leadsQueryArgs }
			this.totalLeads    = this.count

			// Reset selected options so we can repopulate
			this.selectedDataOptions = []

			for (const [ key, value ] of Object.entries(this.export)) {
				if (value && 'type' !== value) {
					this.selectedDataOptions.push(key)
				}
			}

			if (this.export.type) {
				this.format = this.export.type
			}
		},
		/**
		 * Set Query Params
		 * @returns {void}
		 */
		setQueryParams () {
			for (const [ key, value ] of Object.entries(this.exportFilters)) {
				if (value && value.length) {
					this.params[key] = value
				}
			}

			// Format export in the way the endpoint expects it
			if (this.selectedDataOptions && this.selectedDataOptions.length) {
				let exportData = {}

				this.selectedDataOptions.forEach(option => {
					exportData[option] = 1
				})

				exportData.type = this.format

				this.params.export = exportData
			}
		},
		/**
		 * Set Count
		 * @returns {void}
		 */
		setCount () {
			this.fetchingCount = true
			this.fetchTotalCount()
				.then(() => {
					this.fetchingCount = false
					this.totalLeads = this.exportCount
				})
		},
		/**
		 * Send the export request.
		 * @returns {void}
		 */
		sendExportRequest () {
			this.exportLeads(this.params)
				.then(code => {
					if ('204' === String(code)) {
						this.largeExportMessage()
					}
				})
				.catch((e) => {})
				.finally(() => {
					this.$modal.hide('lead-export-modal')
				})
		},
		/**
		 * Run Export
		 * @returns {void}
		 */
		runExport () {
			if (!this.totalLeads || 1 > this.totalLeads) {
				return
			}

			// setup export query params
			this.setQueryParams()

			// check limit
			if (this.totalLeads >= this.exportLimit) {
				return this.goTo('confirmation')
			}

			this.sendExportRequest()
			this.$modal.hide('lead-export-modal')
		},
		/**
		 * Large Export Message
		 * @returns {void}
		 */
		largeExportMessage () {
			this.$store.commit('campaigns/addAlert', {
				type    : 'success',
				message : 'We are working on exporting your data. We will send you an email when the file is ready.',
			})
		},
		/**
		 * Return to options view with current view selected.
		 * @returns {void}
		 */
		narrowResults () {
			this.selectOption('view')
			this.goTo('options')
		},
	},
}
</script>

<style lang="scss">
	@import '../../../assets/scss/variables';

	body .v--modal-overlay.omapi-leadexport__wrapper .v--modal .header {
		background-color: $color_white-alt;
		color: $color_black;
		padding-left: 40px;
		padding-right: 40px;

		.close {
			button {
				width: 13px;
				height: 14px;

				svg {
					path {
						fill: #A0A4A8;
					}
				}
			}
		}
	}

	.omapi-leadexport__wrapper {

		.v--modal {
			border-radius: 0!important;

			.content {
				padding: 20px 40px!important;
			}

			.export-options-slide {
				display: none;
				width: 100%;

				&.active {
					display: block;
				}

				&__action {
					margin-top: 20px;

					.omapi-button {
						font-size: 14px;
						padding: 10px 20px;
					}

					.omapi-link-arrow-after {
						margin-right: 0;
						padding-right: 0;
					}
				}
			}

			.export-option {
				display: flex;
				border-bottom: 1px solid #F7F8FB;
				justify-content: space-between;
				padding-bottom: 20px;
				padding-top: 20px;
				width: 100%;

				&__details {
					&-title {
						font-family: 'Helvetica', sans-serif;
						font-size: 16px;
						font-weight: 700;
						margin-bottom: 5px;
						margin-top: 0;
					}
					&-description {
						color: $font_setting-color;
						font-size: 14px;
						margin: 0;
					}
					&-warning {
						color: $font_setting-color;
						font-size: 16px;
					}
				}

				&__check {
					.export-button {
						background-color: transparent;
						border: 1px solid #ddd;
						border-radius: 50%;
						height: 24px;
						width: 24px;

						&:hover {
							cursor: pointer;
						}

						&:focus {
							outline: none;
						}

						&.selected {
							background-color: $color_blue-primary;
							background-image: url('../../../assets/images/icons/check-white.svg');
							background-repeat: no-repeat;
							background-position: center;
							border-color: $color_blue-primary;
						}

						span {
							display: none;
						}
					}
				}
			}
			.data-options-list {
				display: flex;
				flex-direction: row;
				flex-wrap: wrap;
				width: 100%;
				margin-top: 20px;

				.omapi-checkbox {
					align-items: normal;
					width: 35%;
					flex-direction: row;
				}
			}
		}
	}
</style>
